import React from "react";
import { Icon } from "../core/Icon.jsx";

/** Timeline of the day's schedule — vertical, or horizontal with a carousel when it overflows. */
export function Itinerary({ steps = [], layout = "vertical", tone = "default", style }) {
  const fg = tone === "onDark" ? "var(--fg-on-contrast)" : "var(--fg-primary)";
  const muted = tone === "onDark" ? "color-mix(in srgb, var(--fg-on-contrast) 60%, transparent)" : "var(--fg-muted)";
  const line = tone === "onDark" ? "color-mix(in srgb, var(--fg-on-contrast) 25%, transparent)" : "var(--rule)";

  if (layout === "horizontal") return <HorizontalItinerary steps={steps} fg={fg} muted={muted} line={line} tone={tone} style={style} />;

  return (
    <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", ...style }}>
      {steps.map((s, i) => (
        <li key={i} style={{ display: "grid", gridTemplateColumns: "56px 1fr", gap: "var(--sp-4)", alignItems: "start" }}>
          <div style={{ display: "grid", justifyItems: "center", gap: 6, height: "100%" }}>
            <span style={{ width: 44, height: 44, borderRadius: "50%", background: "var(--accent-soft)", display: "grid", placeItems: "center", color: "var(--accent)" }}>
              <Icon name={s.icon || "circle"} size={18} />
            </span>
            {i < steps.length - 1 && <span style={{ width: 1, flex: 1, minHeight: 34, background: line }} />}
          </div>
          <div style={{ paddingBottom: i < steps.length - 1 ? "var(--sp-6)" : 0 }}>
            <p style={{ margin: 0, fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: "var(--fs-body-l)", color: "var(--accent)" }}>{s.time}</p>
            <h4 style={{ margin: "4px 0 0", fontFamily: "var(--font-title, var(--font-display))", fontWeight: "var(--fw-regular)", fontSize: "var(--fs-title-s)", letterSpacing: "0.01em", color: fg }}>{s.label}</h4>
            {s.detail && <p style={{ margin: "6px 0 0", fontSize: "var(--fs-caption)", lineHeight: "var(--lh-snug)", color: muted }}>{s.detail}</p>}
          </div>
        </li>
      ))}
    </ol>
  );
}

function HorizontalItinerary({ steps, fg, muted, line, tone, style }) {
  const ref = React.useRef(null);
  const [state, setState] = React.useState({ overflow: false, at: 0, count: 1 });

  const measure = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const step = el.firstChild ? el.firstChild.getBoundingClientRect().width + 1 : 1;
    setState({
      overflow: max > 8,
      at: Math.round(el.scrollLeft / step),
      count: Math.max(1, Math.ceil(el.scrollWidth / Math.max(1, el.clientWidth))),
    });
  }, []);

  React.useEffect(() => {
    measure();
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    el.addEventListener("scroll", measure, { passive: true });
    return () => { ro.disconnect(); el.removeEventListener("scroll", measure); };
  }, [measure, steps.length]);

  const nudge = (dir) => () => {
    const el = ref.current;
    if (!el) return;
    const step = el.firstChild ? el.firstChild.getBoundingClientRect().width + 28 : 240;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const arrow = (dir, disabled) => (
    <button type="button" aria-label={dir < 0 ? "Anterior" : "Siguiente"} onClick={nudge(dir)} disabled={disabled} style={{
      width: 44, height: 44, flex: "none", display: "grid", placeItems: "center", cursor: disabled ? "default" : "pointer",
      background: "transparent", border: `1px solid ${disabled ? line : "color-mix(in srgb, var(--accent) 45%, transparent)"}`,
      color: disabled ? muted : "var(--accent)", opacity: disabled ? 0.4 : 1, transition: "opacity var(--dur-fast) var(--ease-standard)",
    }}>
      <Icon name={dir < 0 ? "chevron-left" : "chevron-right"} size={18} />
    </button>
  );

  const atEnd = (() => {
    const el = ref.current;
    return el ? el.scrollLeft >= el.scrollWidth - el.clientWidth - 8 : true;
  })();

  return (
    <div style={{ display: "grid", gap: "var(--sp-5)", width: "100%", minWidth: 0, maxWidth: "100%", overflow: "hidden", ...style }}>
      <div style={{ position: "relative", minWidth: 0, maxWidth: "100%" }}>
        <ol ref={ref} style={{
          listStyle: "none", margin: 0, padding: "0 0 var(--sp-3)", display: "flex", gap: 0, alignItems: "flex-start",
          justifyContent: state.overflow ? "flex-start" : "center",
          overflowX: "auto", scrollSnapType: "x mandatory", scrollbarWidth: "none",
          WebkitMaskImage: state.overflow ? "linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent)" : "none",
          maskImage: state.overflow ? "linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent)" : "none",
        }}>
          {steps.map((s, i) => (
            <li key={i} style={{
              flex: "0 0 auto", width: "clamp(168px, 21vw, 224px)", scrollSnapAlign: "center",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
              textAlign: "center", paddingTop: 4,
            }}>
              <p style={{ margin: 0, height: 22, display: "flex", alignItems: "center", fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: "var(--fs-body-l)", color: "var(--accent)", lineHeight: 1 }}>{s.time}</p>
              <div style={{ position: "relative", width: "100%", height: 44, marginTop: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span aria-hidden="true" style={{
                  position: "absolute", top: "50%", height: 1, background: line,
                  left: i === 0 ? "50%" : 0, right: i === steps.length - 1 ? "50%" : 0,
                }} />
                <span style={{
                  position: "relative", width: 44, height: 44, borderRadius: "50%", display: "grid", placeItems: "center",
                  background: tone === "onDark" ? "color-mix(in srgb, var(--fg-on-contrast) 10%, transparent)" : "var(--accent-soft)",
                  color: "var(--accent)", boxShadow: `0 0 0 6px ${tone === "onDark" ? "var(--bg-contrast)" : "var(--bg-page)"}`,
                }}>
                  <Icon name={s.icon || "circle"} size={18} />
                </span>
              </div>
              <h4 style={{ margin: "12px 0 0", padding: "0 var(--sp-3)", fontFamily: "var(--font-title, var(--font-display))", fontWeight: "var(--fw-regular)", fontSize: "var(--fs-title-s)", letterSpacing: "0.01em", lineHeight: "var(--lh-snug)", color: fg, textWrap: "pretty" }}>{s.label}</h4>
              {s.detail && <p style={{ margin: "6px 0 0", padding: "0 var(--sp-3)", fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: "var(--fs-body)", lineHeight: "var(--lh-snug)", color: muted, textWrap: "pretty" }}>{s.detail}</p>}
            </li>
          ))}
        </ol>
      </div>
      {state.overflow && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "var(--sp-4)" }}>
          {arrow(-1, state.at <= 0)}
          <span style={{ fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: "var(--fs-body)", color: muted }}>Desliza para ver todo</span>
          {arrow(1, atEnd)}
        </div>
      )}
    </div>
  );
}
